import { getRequestAuthPayload } from '@helpers/getRequestAuth';
import type {
  GetEvalConfigResponse,
  GetEvalConfigsResponse,
  GetEvalConfigVersionResponse,
  GetEvalReviewExportResponse,
} from '@responses/evals';
import axios from 'axios';

/**
 * Eval config network layer — Contract Review Bot evaluation period.
 *
 * This file, plus src/mutations/evalConfigs.ts and everything under
 * src/features/eval-config/, is intentionally isolated so the whole
 * feature can be feature-flagged or deleted when the eval is over.
 */

export const fetchEvalConfigs = async (): Promise<GetEvalConfigsResponse> => {
  const networkPayload = await getRequestAuthPayload({
    method: 'get',
    url: '/eval_configs',
  });
  const response = await axios(networkPayload);
  return response.data;
};

/**
 * Tenant-wide review export: every finding and every piece of reviewer
 * feedback, flattened to one row each. Rides the config collection route —
 * see the handler comment in
 * `backend/api/handlers/eval-configs/eval-review-export.handlers.ts` for why.
 * Rows come back in the CSV's own column shape; the caller writes the file.
 */
export const fetchEvalReviewExport = async (options?: {
  configId?: string;
  since?: string;
}): Promise<GetEvalReviewExportResponse> => {
  const networkPayload = await getRequestAuthPayload({
    method: 'get',
    url: '/eval_configs',
    params: {
      export: 'review',
      ...(options?.configId ? { config_id: options.configId } : {}),
      ...(options?.since ? { since: options.since } : {}),
    },
  });
  const response = await axios(networkPayload);
  return response.data;
};

export const fetchEvalConfig = async (id: string): Promise<GetEvalConfigResponse> => {
  const networkPayload = await getRequestAuthPayload({
    method: 'get',
    url: `/eval_configs/${id}`,
  });
  const response = await axios(networkPayload);
  return response.data;
};

export const fetchEvalConfigVersion = async (
  id: string,
  versionNumber: number,
): Promise<GetEvalConfigVersionResponse> => {
  const networkPayload = await getRequestAuthPayload({
    method: 'get',
    url: `/eval_configs/${id}`,
    params: { version: versionNumber },
  });
  const response = await axios(networkPayload);
  return response.data;
};
